import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Plus, Globe, FileText, FileSpreadsheet, RefreshCw, CheckCircle, AlertTriangle, Eye, X } from 'lucide-react';
import { STATUS_VALUES, getStatusLabel } from '../utils/statusTranslations';
import ModalWrapper, { ModalCard } from './ModalWrapper';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { normalizeInstanceLanguage } from '../../../../packages/shared/instanceLanguage.js';
import AiContextDescriptionField, {
  aiContextValueForForm,
  aiContextValueForSave,
} from './InstanceSettings/AiContextDescriptionField';
import WorkshopGoalField from './InstanceSettings/WorkshopGoalField';
import LabelPicker from './InstanceLabels/LabelPicker';
import { getBackendUrl } from '../config/api';


const ModalTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #4CAF50, #2196F3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 15px;

  .icon {
    width: 32px;
    height: 32px;
    color: #4CAF50;
  }
`;

const ModalSubtitle = styled.p`
  margin: 0 0 32px 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 8px;

  .icon {
    width: 16px;
    height: 16px;
    color: rgba(255, 255, 255, 0.6);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }

  option {
    background: #1a1a1a;
    color: white;
  }
`;

const ScenarioSection = styled.div`
  margin-bottom: 24px;
`;

const ScenarioList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const ScenarioItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 8px;

  input {
    flex: 1;
    background: transparent;
    border: none;
    color: white;
    font-size: 0.9rem;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }

  button {
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    transition: color 0.3s ease;

    &:hover {
      color: #ff5252;
    }
  }
`;

const AddButton = styled.button`
  background: rgba(76, 175, 80, 0.2);
  border: 1px solid rgba(76, 175, 80, 0.3);
  color: #4CAF50;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.3);
    transform: translateY(-1px);
  }

  .icon {
    width: 16px;
    height: 16px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 32px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &.primary {
    background: linear-gradient(135deg, #4CAF50, #2196F3);
    border: none;
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 32px rgba(76, 175, 80, 0.3);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .icon {
    width: 20px;
    height: 20px;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #ff5252;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  color: #4CAF50;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.9rem;
`;

const SheetImportSection = styled.div`
  background: rgba(76, 175, 80, 0.05);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
`;

const SheetSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #4CAF50;
  }

  span {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.1);
    padding: 2px 8px;
    border-radius: 4px;
  }
`;

const SheetInputRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const SheetButton = styled.button`
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  white-space: nowrap;

  &.preview {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.15);
    }
  }

  &.create {
    background: linear-gradient(135deg, #4CAF50, #2E7D32);
    border: none;
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const PreviewBox = styled.div`
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 8px;
  padding: 12px;
  margin-top: 12px;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  font-size: 0.9rem;

  strong {
    color: #4CAF50;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;

  &::before, &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  span {
    padding: 0 16px;
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.85rem;
  }
`;

function CreateInstanceModal({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: STATUS_VALUES.PRE_WORKSHOP, // Default to pre-workshop (visible to users)
    language: 'fi',
    instance_type: 'live',
    main_scenarios: [''],
    settings: { language: 'fi', workshopGoal: '' },
    ai_context_description: aiContextValueForForm(''),
    tags: [], // at least one label is required (#1209)
  });
  const [workshopGoal, setWorkshopGoal] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Sheet import state
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetPreview, setSheetPreview] = useState(null);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'language') {
        const normalized = normalizeInstanceLanguage(value);
        next.language = normalized;
        next.settings = { ...prev.settings, language: normalized };
      }
      return next;
    });
  };

  const handleScenarioChange = (index, value) => {
    const newScenarios = [...formData.main_scenarios];
    newScenarios[index] = value;
    setFormData(prev => ({
      ...prev,
      main_scenarios: newScenarios
    }));
  };

  const addScenario = () => {
    setFormData(prev => ({
      ...prev,
      main_scenarios: [...prev.main_scenarios, '']
    }));
  };

  const removeScenario = (index) => {
    const newScenarios = formData.main_scenarios.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      main_scenarios: newScenarios.length > 0 ? newScenarios : ['']
    }));
  };

  // Preview sheet config (uses a temporary preview endpoint)
  const handleSheetPreview = async () => {
    if (!sheetUrl || sheetLoading) return;

    setSheetError(null);
    setSheetPreview(null);
    setSheetLoading(true);

    try {
      const baseUrl = getBackendUrl();
      // Use preview-only endpoint that doesn't require instanceId
      const response = await fetch(`${baseUrl}/api/sheets/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sheetUrl })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Esikatselu epäonnistui');
      }

      setSheetPreview(data.config);
    } catch (err) {
      setSheetError(err.message);
    } finally {
      setSheetLoading(false);
    }
  };

  // Create instance from sheet
  const handleCreateFromSheet = async () => {
    if (!sheetPreview || sheetLoading) return;

    setSheetLoading(true);
    setSheetError(null);

    try {
      const baseUrl = getBackendUrl();

      if (formData.tags.length === 0) throw new Error(t('instanceLabels.required'));
      // Step 1: Create a minimal instance first
      const instanceTitle = sheetPreview.instance?.title || 'Uusi instanssi';
      const createResponse = await fetch(`${baseUrl}/api/instances`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: instanceTitle,
          description: sheetPreview.instance?.description || '',
          status: STATUS_VALUES.PRE_WORKSHOP,
          instance_type: sheetPreview.instance?.instance_type || 'live',
          tags: formData.tags,
        })
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.error || 'Instanssin luominen epäonnistui');
      }

      const newInstance = await createResponse.json();

      // Step 2: Sync config from sheet to the new instance
      const syncResponse = await fetch(
        `${baseUrl}/api/kuutio-instances/${newInstance.id}/sync-from-sheet`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sheetUrl })
        }
      );

      if (!syncResponse.ok) {
        const errorData = await syncResponse.json();
        throw new Error(errorData.error || 'Synkronointi epäonnistui');
      }

      const syncResult = await syncResponse.json();

      setSuccess(true);

      // Show success and close
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(newInstance);
        }
        onClose();
      }, 1500);

    } catch (err) {
      setSheetError(err.message);
    } finally {
      setSheetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const goal = workshopGoal.trim();
      if (!goal) {
        throw new Error(t('instanceSettingsModal.workshopGoalRequired')
          || 'Workshop goal is required');
      }
      if (formData.tags.length === 0) throw new Error(t('instanceLabels.required'));
      // Filter out empty scenarios
      const filteredData = {
        ...formData,
        main_scenarios: formData.main_scenarios.filter(s => s.trim() !== ''),
        ai_context_description: aiContextValueForSave(formData.ai_context_description),
        settings: {
          ...(formData.settings || {}),
          language: formData.language || 'fi',
          workshopGoal: goal,
        },
      };

      const baseUrl = getBackendUrl();
      const response = await fetch(`${baseUrl}/api/instances`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(filteredData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create instance');
      }

      const newInstance = await response.json();
      setSuccess(true);

      // Wait a moment to show success message
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(newInstance);
        }
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message);
      console.error('Error creating instance:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper visible onClose={onClose} zIndex={10000} blur={5} opacity={0.8}>
      <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
        <ModalCard.Body style={{ padding: '32px 40px 40px' }}>

        <ModalTitle>
          <Globe className="icon" />
          Luo uusi Kuutio-instanssi
        </ModalTitle>

        <ModalSubtitle>
          Luo uusi demokraattisen osallistumisen työpaja. Kuutio-instanssi on itsenäinen työpaja,
          joka voi oppia ja jakaa tietoa muiden instanssien kanssa.
        </ModalSubtitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>Instanssi luotu onnistuneesti!</SuccessMessage>}

        {/* Sheet Import Section */}
        <SheetImportSection>
          <SheetSectionHeader>
            <FileSpreadsheet size={20} style={{ color: '#4CAF50' }} />
            <h3>Luo Google Sheetistä</h3>
            <span>Suositeltu</span>
          </SheetSectionHeader>

          <SheetInputRow>
            <Input
              type="text"
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              disabled={sheetLoading || loading}
              style={{ flex: 1 }}
            />
            <SheetButton
              type="button"
              className="preview"
              onClick={handleSheetPreview}
              disabled={!sheetUrl || sheetLoading || loading}
            >
              {sheetLoading && !sheetPreview ? (
                <RefreshCw size={16} className="spinning" />
              ) : (
                <Eye size={16} />
              )}
              Esikatselu
            </SheetButton>
          </SheetInputRow>

          {sheetError && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#f44336',
              fontSize: '0.85rem'
            }}>
              <AlertTriangle size={16} />
              {sheetError}
            </div>
          )}

          {sheetPreview && (
            <PreviewBox>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                <CheckCircle size={16} style={{ color: '#4CAF50' }} />
                <strong style={{ color: '#4CAF50' }}>{sheetPreview.instance?.title || 'Kuutio'}</strong>
              </div>
              <PreviewGrid>
                <div>Skenaariot: <strong>{sheetPreview.scenarioCount}</strong></div>
                <div>Muuttujat: <strong>{sheetPreview.variableCount}</strong></div>
                <div>Tyyppi: <strong>{sheetPreview.instance?.instance_type || 'live'}</strong></div>
              </PreviewGrid>
              {sheetPreview.scenarios?.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {sheetPreview.scenarios.join(' • ')}
                </div>
              )}
              <SheetButton
                type="button"
                className="create"
                onClick={handleCreateFromSheet}
                disabled={sheetLoading || loading}
                style={{ marginTop: '12px', width: '100%', justifyContent: 'center' }}
              >
                {sheetLoading ? (
                  <>
                    <RefreshCw size={16} className="spinning" />
                    Luodaan...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Luo instanssi Sheetistä
                  </>
                )}
              </SheetButton>
            </PreviewBox>
          )}
        </SheetImportSection>

        <Divider>
          <span>tai luo manuaalisesti</span>
        </Divider>

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              <FileText className="icon" />
              Instanssin nimi *
            </Label>
            <Input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="esim. Helsinki Kaupunkisuunnittelu 2024"
              required
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <FileText className="icon" />
              Kuvaus
            </Label>
            <TextArea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Kuvaile työpajan tavoitteet ja osallistujat..."
              disabled={loading}
            />
          </FormGroup>

          <WorkshopGoalField
            value={workshopGoal}
            onChange={setWorkshopGoal}
            disabled={loading}
            required
          />

          <AiContextDescriptionField
            value={formData.ai_context_description}
            onChange={(value) => setFormData((prev) => ({ ...prev, ai_context_description: value }))}
            disabled={loading}
          />

          <FormGroup>
            <Label>{t('instanceLabels.title')} *</Label>
            <LabelPicker value={formData.tags} disabled={loading}
              onChange={(tags) => setFormData((prev) => ({ ...prev, tags }))} />
          </FormGroup>

          <FormGroup>
            <Label>Status *</Label>
            <Select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              disabled={loading}
              required
            >
              <option value={STATUS_VALUES.DRAFT}>{getStatusLabel(STATUS_VALUES.DRAFT)}</option>
              <option value={STATUS_VALUES.PRE_WORKSHOP}>{getStatusLabel(STATUS_VALUES.PRE_WORKSHOP)}</option>
              <option value={STATUS_VALUES.RUNNING}>{getStatusLabel(STATUS_VALUES.RUNNING)}</option>
              <option value={STATUS_VALUES.POST_WORKSHOP}>{getStatusLabel(STATUS_VALUES.POST_WORKSHOP)}</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>{t('instanceSettings.language')}</Label>
            <Select
              name="language"
              value={formData.language}
              onChange={handleInputChange}
              disabled={loading}
            >
              <option value="fi">{t('instanceSettingsModal.langFi')}</option>
              <option value="en">{t('instanceSettingsModal.langEn')}</option>
              <option value="sv">{t('instanceSettingsModal.langSv')}</option>
            </Select>
            <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem', color: 'rgba(255,255,255,0.65)' }}>
              {t('instanceSettings.languageHelp')}
            </p>
          </FormGroup>

          <FormGroup>
            <Label>
              <Globe className="icon" />
              Tyyppi *
            </Label>
            <Select
              name="instance_type"
              value={formData.instance_type}
              onChange={handleInputChange}
              disabled={loading}
              required
            >
              <option value="live">Live - Fyysinen työpaja (iPadit)</option>
              <option value="online">Online - Etäosallistujat</option>
            </Select>
          </FormGroup>

          <ScenarioSection>
            <Label>
              Skenaariot
            </Label>
            <ScenarioList>
              {formData.main_scenarios.map((scenario, index) => (
                <ScenarioItem key={index}>
                  <input
                    type="text"
                    value={scenario}
                    onChange={(e) => handleScenarioChange(index, e.target.value)}
                    placeholder={`Skenaario ${index + 1}`}
                    disabled={loading}
                  />
                  {formData.main_scenarios.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeScenario(index)}
                      disabled={loading}
                    >
                      <X style={{ width: 16, height: 16 }} />
                    </button>
                  )}
                </ScenarioItem>
              ))}
            </ScenarioList>
            <AddButton type="button" onClick={addScenario} disabled={loading}>
              <Plus className="icon" />
              Lisää skenaario
            </AddButton>
          </ScenarioSection>

          <ButtonGroup>
            <Button
              type="button"
              className="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Peruuta
            </Button>
            <Button
              type="submit"
              className="primary"
              disabled={loading || !formData.title.trim()}
            >
              {loading ? 'Luodaan...' : 'Luo instanssi'}
            </Button>
          </ButtonGroup>
        </form>
        </ModalCard.Body>
      </ModalCard>
    </ModalWrapper>
  );
}

export default CreateInstanceModal;